import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { showLoader } from '../../utils/showloader'
import { getSearchResult } from '../../services/WebServices'
import { HomeOutlined, AppstoreOutlined, FileTextOutlined, UserOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import BreadCrumb from '../BreadCrumb'
import { Tag, Typography, Empty, Badge } from 'antd'
import './search-result.css'
import { useTranslation } from 'react-i18next';

const SearchResult = () => {
    const { t } = useTranslation();
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [searchResults, setSearchResults] = useState({ spaceDetails: [], requests: [] })
    const [loading, setLoading] = useState(false)
    const [activeTab, setActiveTab] = useState("space")
    let { userInfo } = useSelector((state) => state.user);

    let path = "/app/dashboard";
    if (userInfo.isAdmin) {
        path = "/app/dashboard"
    }

    const searchQuery = searchParams.get('q')

    useEffect(() => {
        if (searchQuery) {
            fetchSearchResults(searchQuery)
        }
    }, [searchQuery])

    const breadCrumbItems = [
        {
            title: <HomeOutlined />,
            path: path,
        },
        {
            title: t('Search Result'),
        },
    ];

    const fetchSearchResults = async (query) => {
        try {
            setLoading(true)
            showLoader(true)
            const response = await getSearchResult(query)
            if (response.data?.result) {
                setSearchResults({ ...response.data?.result })
            }
        } catch (error) {
            setSearchResults({ spaceDetails: [], requests: [] })
        } finally {
            setLoading(false)
            showLoader(false)
        }
    }

    const handleNavigate = (type, record) => {
        let url = "";
        if (type === "space") {
            url = `/app/space/${record.spaceId}/documents`
        } else {
            url = `/app/space/${record.spaceId}/approve/${record.workflowId}/${record.requestId}`
        }
        navigate(url)
    }

    const onChangeTab = (value) => {
        setActiveTab(value);
    };

    // Get status color based on status type
    const getStatusColor = (status) => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'approval':
                return 'processing';
            case 'posted':
                return 'success';
            case 'draft':
                return 'default';
            case 'deleted':
                return 'error';
            case 'archived':
                return 'warning';
            default:
                return 'default';
        }
    };

    const spacesCount = searchResults.spaceDetails?.length || 0;
    const requestsCount = searchResults.requests?.length || 0;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="sticky-space-header">
                {/* Breadcrumb */}
                <div className="mb-2">
                    <BreadCrumb items={breadCrumbItems} marginb="16px" />
                </div>
                <div className="mb-4">
                    <div className="flex flex-wrap sm:items-center sm:justify-between gap-4">
                        <Typography.Title level={4} className="!m-0 text-gray-900 items-center justify-center">
                            {t('Search Result:')} <span className="search-query-highlight">"{searchQuery}"</span>
                        </Typography.Title>
                        <div className="search-summary">
                            {t('Found')} {spacesCount + requestsCount} results
                        </div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="mb-4">
                    <div className="search-tabs">
                        <button
                            className={`search-tab ${activeTab === "space" ? "active" : ""}`}
                            onClick={() => onChangeTab("space")}
                        >
                            <AppstoreOutlined className="tab-icon" />
                            <span>{t('Spaces')}</span>
                            <Badge count={spacesCount} showZero color={activeTab === "space" ? "#141BCB" : "#8c8c8c"} />
                        </button>
                        <button
                            className={`search-tab ${activeTab === "request" ? "active" : ""}`}
                            onClick={() => onChangeTab("request")}
                        >
                            <FileTextOutlined className="tab-icon" />
                            <span>{t('Requests')}</span>
                            <Badge count={requestsCount} showZero color={activeTab === "request" ? "#141BCB" : "#8c8c8c"} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="px-4 sm:px-6 lg:px-8 pb-4 mx-auto search-result">
                {/* Content */}
                {activeTab === "space" ? (
                    <div className="search-results-container">
                        {spacesCount > 0 ? (
                            <div className="results-grid">
                                {searchResults.spaceDetails.map((result, index) => (
                                    <div
                                        key={index}
                                        className="result-card"
                                        onClick={() => handleNavigate("space", result)}
                                    >
                                        <div className="result-card-icon space-icon">
                                            <AppstoreOutlined />
                                        </div>
                                        <div className="result-card-content">
                                            <div className="result-card-title">{result.name}</div>
                                            {result.description && (
                                                <div className="result-card-description">{result.description}</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Empty
                                description={t('No spaces found')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                className="empty-results"
                            />
                        )}
                    </div>
                ) : (
                    <div className="search-results-container">
                        {requestsCount > 0 ? (
                            <div className="results-grid">
                                {searchResults.requests.map((result, index) => (
                                    <div
                                        key={index}
                                        className="result-card"
                                        onClick={() => handleNavigate("request", result)}
                                    >
                                        <div className="result-card-icon request-icon">
                                            <FileTextOutlined />
                                        </div>
                                        <div className="result-card-content">
                                            <div className="result-card-header">
                                                <div className="result-card-title">{result.caseId}</div>
                                                <Tag color={getStatusColor(result.status)}>
                                                    {result.status}
                                                </Tag>
                                            </div>
                                            {result.createdBy?.name && (
                                                <div className="result-card-meta">
                                                    <UserOutlined className="meta-icon" />
                                                    <span>{result.createdBy.name}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Empty
                                description={t('No requests found')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                className="empty-results"
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default SearchResult